// API service for custom critter types
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

class CritterTypeApi {
  constructor() {
    this.broadcasterLogin = null
  }

  setBroadcasterLogin(login) {
    this.broadcasterLogin = login
  }

  // Get all critter types (defaults + custom) for the broadcaster
  async getAllTypes() {
    if (!this.broadcasterLogin) {
      console.warn('Broadcaster login not set')
      return {}
    }

    try {
      const response = await fetch(
        `${API_BASE}/twitch/api/broadcaster/${this.broadcasterLogin}/critter-types`
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch critter types: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching critter types:', error)
      return {}
    }
  }

  // Get only custom critter types for the broadcaster
  async getCustomTypes() {
    if (!this.broadcasterLogin) {
      console.warn('Broadcaster login not set')
      return []
    }

    try {
      const response = await fetch(
        `${API_BASE}/twitch/api/broadcaster/${this.broadcasterLogin}/critter-types/custom`
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch custom critter types: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching custom critter types:', error)
      return []
    }
  }

  // Create a new custom critter type with image uploads
  async createType(typeKey, name, mouthClosedFile, mouthOpenFile, smilingFile = null) {
    if (!this.broadcasterLogin) {
      throw new Error('Broadcaster login not set')
    }

    const formData = new FormData()
    formData.append('typeKey', typeKey)
    formData.append('name', name)
    formData.append('mouthClosed', mouthClosedFile)
    formData.append('mouthOpen', mouthOpenFile)
    if (smilingFile) {
      formData.append('smiling', smilingFile)
    }

    const response = await fetch(
      `${API_BASE}/twitch/api/broadcaster/${this.broadcasterLogin}/critter-types`,
      {
        method: 'POST',
        body: formData
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to create critter type')
    }

    return await response.json()
  }

  // Update an existing custom critter type
  async updateType(typeKey, updates) {
    if (!this.broadcasterLogin) {
      throw new Error('Broadcaster login not set')
    }

    const formData = new FormData()
    if (updates.name) formData.append('name', updates.name)
    if (updates.mouthClosedFile) formData.append('mouthClosed', updates.mouthClosedFile)
    if (updates.mouthOpenFile) formData.append('mouthOpen', updates.mouthOpenFile)
    if (updates.smilingFile) formData.append('smiling', updates.smilingFile)

    const response = await fetch(
      `${API_BASE}/twitch/api/broadcaster/${this.broadcasterLogin}/critter-types/${typeKey}`,
      {
        method: 'PUT',
        body: formData
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to update critter type')
    }

    return await response.json()
  }

  // Delete a custom critter type
  async deleteType(typeKey) {
    if (!this.broadcasterLogin) {
      throw new Error('Broadcaster login not set')
    }

    const response = await fetch(
      `${API_BASE}/twitch/api/broadcaster/${this.broadcasterLogin}/critter-types/${typeKey}`,
      {
        method: 'DELETE'
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to delete critter type')
    }

    return true
  }
}

export const critterTypeApi = new CritterTypeApi()
